import { FALLBACK_PRODUCTS, ScannedProduct } from './recentPurchaseData'

/**
 * Scans the live page DOM to extract one or more purchasable products.
 * Strategy (in order of preference):
 *   1. JSON-LD Product / Offer (schema.org) — most reliable.
 *   2. DOM scan: look for price-like text near CTA buttons and headings.
 *   3. Fallback: return the default product distribution.
 */

const PRICE_REGEX = /(\$|USD\s?|US\$)\s?(\d{1,4}(?:[.,]\d{2})?)/i
const CTA_KEYWORDS = [
  'add to cart',
  'añadir al carrito',
  'agregar al carrito',
  'buy',
  'comprar',
  'checkout',
  'order',
  'order now',
  'get access',
  'instant access',
  'acceso inmediato',
  'obtener acceso',
  'comenzar',
]
const EDITION_KEYWORDS = [
  'pack despertar',
  'physical book',
  'edición digital',
  'digital edition',
  'e-book',
  'ebook',
  'edición física',
  'edicion fisica',
  'libro físico',
  'libro fisico',
]

function parsePrice(raw: string): { number: number; text: string } | null {
  const match = raw.match(PRICE_REGEX)
  if (!match) return null
  const num = parseFloat(match[2].replace(',', '.'))
  if (isNaN(num) || num <= 0 || num > 9999) return null
  return { number: num, text: `${num.toFixed(2).replace(/\.00$/, '')}` }
}

/**
 * Returns true if the element (or any of its ancestors up to `stopAt`)
 * has a line-through text-decoration. Used to filter out struck-through
 * "original" prices and keep the actual offer price.
 */
function isStruckThrough(el: Element, stopAt: Element): boolean {
  let cursor: Element | null = el
  while (cursor && cursor !== stopAt.parentElement) {
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      const decoration = window.getComputedStyle(cursor).textDecorationLine
      if (decoration && decoration.includes('line-through')) return true
    }
    if (
      cursor.className &&
      typeof cursor.className === 'string' &&
      cursor.className.includes('line-through')
    ) {
      return true
    }
    cursor = cursor.parentElement
  }
  return false
}

/**
 * Finds the most likely "current offer price" inside a container,
 * ignoring any struck-through (line-through) prices.
 */
function findOfferPrice(
  container: Element,
): { number: number; text: string } | null {
  // Collect every element whose own text content matches a price.
  const candidates: Array<{
    element: Element
    number: number
    text: string
  }> = []

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_ELEMENT,
    null,
  )
  let node = walker.currentNode as Element | null
  while (node) {
    // Use innerText/textContent of just this element (will include children).
    const text = (node as HTMLElement).innerText || node.textContent || ''
    const match = text.match(PRICE_REGEX)
    if (match) {
      const num = parseFloat(match[2].replace(',', '.'))
      if (!isNaN(num) && num > 0 && num < 9999) {
        // Prefer the deepest element that contains exactly this price text
        // (so we attach the line-through check to the right node).
        if (!isStruckThrough(node, container)) {
          candidates.push({
            element: node,
            number: num,
            text: `${num.toFixed(2).replace(/\.00$/, '')}`,
          })
        }
      }
    }
    node = walker.nextNode() as Element | null
  }

  if (!candidates.length) return null

  // Prefer the smallest non-struck-through price (typical for discount layouts
  // where the deal price is the lower number).
  candidates.sort((a, b) => a.number - b.number)
  return { number: candidates[0].number, text: candidates[0].text }
}

function findNearbyHeading(el: Element): string | null {
  let cursor: Element | null = el
  for (let i = 0; i < 6 && cursor; i++) {
    const heading = cursor.querySelector('h1, h2, h3, h4')
    if (heading && heading.textContent) {
      const text = heading.textContent.trim()
      if (text.length > 3 && text.length < 120) return text
    }
    cursor = cursor.parentElement
  }
  return null
}

function findNearbyImage(el: Element): string | null {
  let cursor: Element | null = el
  for (let i = 0; i < 6 && cursor; i++) {
    const img = cursor.querySelector('img')
    if (img && img.src && !img.src.startsWith('data:')) {
      return img.src
    }
    cursor = cursor.parentElement
  }
  return null
}

function findEditionLabel(el: Element): string | null {
  let cursor: Element | null = el
  for (let i = 0; i < 6 && cursor; i++) {
    const text = (cursor.textContent || '').toLowerCase()
    for (const kw of EDITION_KEYWORDS) {
      if (text.includes(kw)) {
        // Try to preserve original casing from the DOM.
        const re = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')
        const match = (cursor.textContent || '').match(re)
        return match ? match[0] : kw
      }
    }
    cursor = cursor.parentElement
  }
  return null
}

function scanJsonLd(): ScannedProduct[] {
  const products: ScannedProduct[] = []
  const scripts = document.querySelectorAll(
    'script[type="application/ld+json"]',
  )
  scripts.forEach((s) => {
    try {
      const data = JSON.parse(s.textContent || '')
      const items = Array.isArray(data) ? data : [data]
      items.forEach((item) => {
        const type = item['@type']
        const isProduct =
          type === 'Product' ||
          (Array.isArray(type) && type.includes('Product'))
        if (!isProduct) return
        const offers = Array.isArray(item.offers)
          ? item.offers
          : item.offers
            ? [item.offers]
            : []
        offers.forEach((offer: any) => {
          const priceNumber = parseFloat(offer.price)
          if (!priceNumber || isNaN(priceNumber)) return
          const currency = offer.priceCurrency || 'USD'
          const symbol = currency === 'USD' ? '$' : ''
          products.push({
            title: item.name || 'Producto',
            editionLabel: offer.name || null,
            priceNumber,
            priceText: `${symbol}${priceNumber}`,
            thumbnailUrl: Array.isArray(item.image)
              ? item.image[0]
              : item.image,
          })
        })
        // If no offers but item has a price field directly
        if (!offers.length && item.offers?.price) {
          const priceNumber = parseFloat(item.offers.price)
          if (priceNumber) {
            products.push({
              title: item.name || 'Producto',
              editionLabel: null,
              priceNumber,
              priceText: `$${priceNumber}`,
              thumbnailUrl: Array.isArray(item.image)
                ? item.image[0]
                : item.image,
            })
          }
        }
      })
    } catch {
      /* ignore parse errors */
    }
  })
  return products
}

function scanDom(): ScannedProduct[] {
  if (typeof document === 'undefined') return []
  const products: ScannedProduct[] = []
  const seenPrices = new Set<string>()

  // Find candidate purchase sections by looking for CTAs.
  const allElements = Array.from(
    document.querySelectorAll('button, a, [role="button"]'),
  )
  const ctaElements = allElements.filter((el) => {
    const text = (el.textContent || '').trim().toLowerCase()
    if (!text || text.length > 60) return false
    return CTA_KEYWORDS.some((kw) => text.includes(kw))
  })

  ctaElements.forEach((cta) => {
    // Walk up to find a container that likely holds the product.
    let container: Element | null = cta.parentElement
    let bestContainer: Element | null = null
    for (let i = 0; i < 8 && container; i++) {
      const text = container.textContent || ''
      if (PRICE_REGEX.test(text)) {
        bestContainer = container
        break
      }
      container = container.parentElement
    }
    if (!bestContainer) return

    // Use the smarter offer-price finder that skips struck-through prices.
    const priceInfo =
      findOfferPrice(bestContainer) ||
      parsePrice(bestContainer.textContent || '')
    if (!priceInfo) return

    const key = `${priceInfo.number}`
    if (seenPrices.has(key)) return
    seenPrices.add(key)

    const title = findNearbyHeading(bestContainer) || 'Producto'
    const editionLabel = findEditionLabel(bestContainer)
    const thumbnailUrl = findNearbyImage(bestContainer)

    products.push({
      title,
      editionLabel,
      priceNumber: priceInfo.number,
      priceText: priceInfo.text,
      thumbnailUrl,
    })
  })

  return products
}

export function scanProducts(): ScannedProduct[] {
  if (typeof document === 'undefined') return FALLBACK_PRODUCTS
  try {
    const fromJsonLd = scanJsonLd()
    if (fromJsonLd.length > 0) return fromJsonLd

    const fromDom = scanDom()
    if (fromDom.length > 0) return fromDom

    return FALLBACK_PRODUCTS
  } catch {
    return FALLBACK_PRODUCTS
  }
}
